import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Briefcase, TrendingUp, Compass, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Briefcase,
    n: "01",
    title: "Executive Search",
    href: "/executive-search",
    body:
      "Discreet, retained search for CEO, COO, CCO, MD and board appointments — for founders, PE/VC-backed scale-ups and SMEs.",
    points: ["Retained mandates", "Board & C-suite", "UK-focused reach"],
  },
  {
    icon: TrendingUp,
    n: "02",
    title: "Commercial Leadership Hiring",
    href: "/commercial-leadership",
    body:
      "Senior commercial operators — Commercial Directors, VPs of Sales, VPs of Growth — built around your GTM and revenue thesis.",
    points: ["GTM-aligned hiring", "Revenue leadership", "Founder-mode operators"],
  },
  {
    icon: Compass,
    n: "03",
    title: "Talent Advisory",
    href: "/talent-advisory",
    body:
      "Org design, leadership benchmarking and team architecture for founders preparing for the next funding round, scale phase or exit.",
    points: ["Org & comp design", "Leadership mapping", "Scale-stage advisory"],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#0E2750] py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="lg:col-span-5">
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-mono-eyebrow text-[#C9A876]"
            >
              ◆ Our Practice
            </motion.span>
            <motion.h2
              data-testid="services-heading"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white"
            >
              Three disciplines.<br />
              <span className="text-slate-400">One commitment to outcomes.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-slate-400 leading-relaxed">
              Arcpoint operates as an extension of your leadership team —
              quietly placing the operators who shape commercial outcomes,
              operational rigour and technology direction.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative bg-[#0E2750] p-8 md:p-10 hover:bg-white/[0.025] transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-10">
                  <Icon size={28} strokeWidth={1.25} className="text-[#C9A876]" />
                  <span className="font-mono-eyebrow text-slate-500">{s.n}</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight mb-4">
                  {s.title}
                </h3>
                <p className="text-sm md:text-base text-slate-400 leading-relaxed mb-8">
                  {s.body}
                </p>
                <ul className="space-y-2 mb-10">
                  {s.points.map((p) => (
                    <li
                      key={p}
                      className="text-xs uppercase tracking-[0.18em] text-slate-500 flex items-center gap-3"
                    >
                      <span className="block h-px w-5 bg-[#C9A876]/60" />
                      {p}
                    </li>
                  ))}
                </ul>
                <Link
                  to={s.href}
                  data-testid={`service-learn-more-${i}`}
                  className="inline-flex items-center gap-2 text-[#C9A876] text-sm tracking-wide hover:gap-3 transition-all duration-300"
                >
                  <span>Learn more</span>
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
